import { SessionStorage } from 'quasar'

// Login handoff: moves a freshly-minted session token from the external-login
// callback handler to the login page WITHOUT putting it in a URL.
//
// The external login flows (Microsoft, SSO) land the browser back on the app
// with the token inside a `_callback` blob. The callback handler used to hand
// it onward by navigating to `/login?token=<jwt>` — a full page load, so the
// raw session JWT was sent to the server as a query string and recorded in the
// access logs in plaintext. Anyone with log read access could lift a live token
// (they are valid for 12h and are not revocable).
//
// sessionStorage keeps it in the browser: same-origin only, never sent to the
// server, and scoped to the tab so it does not outlive the sign-in. It is the
// same trust boundary the app already uses for `api_token`, so this adds no new
// exposure class — it removes one. Quasar's SessionStorage wrapper is used
// rather than raw sessionStorage because it already degrades to a no-op store
// when the browser blocks site data, matching how `api_token` is stored.
const LOGIN_HANDOFF_KEY = 'tm_login_handoff'

// LOGIN_HANDOFF_TTL_MS bounds how long a parked token stays usable. The stash
// and the pickup are one navigation apart, so this is generous. Without it the
// entry lives until the tab closes AND survives browser session restore, so a
// token orphaned by a stalled load could silently sign someone in hours later —
// on a shared machine, as whoever started the abandoned login.
const LOGIN_HANDOFF_TTL_MS = 60 * 1000

// stashLoginHandoffToken parks a token for the login page to pick up. Returns
// false when storage is unavailable, so the caller can fall back to a less
// private path rather than dropping the token on the floor and stranding the
// user on a login form with no explanation.
export function stashLoginHandoffToken(token) {
	if (!token) {
		return false
	}
	try {
		SessionStorage.set(LOGIN_HANDOFF_KEY, { token, ts: Date.now() })
		// Quasar swaps in an empty store when the browser blocks site data, so a
		// successful set() is not proof it persisted. Read it back.
		return !!readHandoff()
	} catch {
		return false
	}
}

function readHandoff() {
	try {
		const entry = SessionStorage.getItem(LOGIN_HANDOFF_KEY)
		if (!entry || typeof entry.token !== 'string' || !entry.token) {
			return null
		}
		if (!Number.isFinite(entry.ts) || Date.now() - entry.ts > LOGIN_HANDOFF_TTL_MS) {
			clearLoginHandoffToken()
			return null
		}
		return entry.token
	} catch {
		return null
	}
}

// takeLoginHandoffToken returns the parked token and clears it. Single use: the
// token is consumed on read so a later reload or a restored session cannot
// replay it.
export function takeLoginHandoffToken() {
	const token = readHandoff()
	clearLoginHandoffToken()
	return token
}

// clearLoginHandoffToken drops any parked token. Called on logout so an
// abandoned handoff cannot sign the next person in on a shared browser.
export function clearLoginHandoffToken() {
	try {
		SessionStorage.remove(LOGIN_HANDOFF_KEY)
	} catch {
		// Nothing to do; an unreadable store is already effectively empty.
	}
}
